/**
 * The v1 scene operations (spec 021). Each is pure: it never mutates `doc`,
 * returns a new document whose untouched sub-objects keep their identity, and
 * takes every new id from `ctx.newId()` in order (listed in `createdIds`).
 * `apply` assumes args that passed `inputSchema` (`runOperation` checks it)
 * and checks everything the schema cannot: ranges, references, media.
 */
import type {
  Animation, ChoiceOption, ChoiceSettings, Dialog, DialogLine, Keyframe, KeyframeEasing, Orientation, OrientationLayout, Position,
  ResponsiveLayout, Sound, SoundType, Sprite, SpriteSize, SpriteState, Transition,
} from '../@types';
import {
  getBasisOrientation, getResponsivePosition, getResponsiveTransform, isDerived, removeFieldOverride,
  setAtRestFlip, setOrientationTransform, setPositionField, withDerived,
} from '../utils/responsiveLayout';
import { applyMotionPreset, DEFAULT_ANIMATION_DURATION_MS, motionDurationMs, type MotionPreset } from '../utils/motionPresets';
import { KEYFRAME_EASINGS, type NewKeyframeId } from '../utils/keyframeWrite';
import {
  clampSoundTiming, computeDialogTimeline, DEFAULT_LINE_DURATION_S, fitLineToVoice, isKnownVoiceLength, lineInMs, lineTransitions, soundTypeChange, transitionDurationMs, SOUND_TYPE_DEFAULTS, VOICE_PAUSE_S,
} from '../utils/sceneTiming';
import { CHOICE_TEXT_MAX, CHOICE_TIMER_MAX_S, MAX_CHOICES, validateChoices, validateChoiceSettings } from '../utils/choices';
import { normaliseSceneName, SCENE_NAME_MAX_LENGTH } from '../utils/sceneName';
import { toPlayableScene } from '../runtime/playableScene';
import { sizeFromImage } from '../utils/spriteSize';
import { compensateHinge } from '../utils/spriteHinge';
import { validateStatePlayback } from '../utils/spriteSheet';
import { backgroundDefaultState, backgroundStates, writeBackgroundStates } from '../utils/backgroundStates';
import { PLAYBACK_ARG_SCHEMA, playbackFromArg, type PlaybackArg } from './playbackArg';
import { ORIENTATIONS, backToFront, depthZIndices, derivePosition, resolvePlacement } from './placement';
import { EASES, EASE_TO_GSAP, RANGES, fmtMs, fmtS, secToMs } from './units';
import type {
  Ease, JsonSchema, Motion, OperationContext, OperationError, OperationResult, Placement, SceneDocument,
  SceneOperation, SceneTransitionArg,
} from './types';

// ─── Schema builders ────────────────────────────────────────────────────────

const str = (description: string): JsonSchema => ({ type: 'string', description });
const num = (description: string): JsonSchema => ({ type: 'number', description });
const int = (description: string): JsonSchema => ({ type: 'integer', description });
const bool = (description: string): JsonSchema => ({ type: 'boolean', description });
const oneOf = (values: readonly string[], description: string): JsonSchema => ({ type: 'string', enum: values, description });
const obj = (properties: Record<string, JsonSchema>, required: string[] = [], description?: string): JsonSchema => ({
  type: 'object',
  ...(description ? { description } : {}),
  properties,
  required,
  additionalProperties: false,
});
const arr = (items: JsonSchema, description: string, minItems?: 0 | 1): JsonSchema => ({
  type: 'array', description, items, ...(minItems !== undefined ? { minItems } : {}),
});

const rangeText = (r: readonly [number, number]) => `${r[0]}–${r[1]}`;
const EASE_SCHEMA = oneOf(EASES, 'Easing, one per option of the editor\'s ease picker: linear, easeIn, easeOut, easeInOut, strongOut, strongInOut, elastic or bounceOut.');
const KEY_EASE_SCHEMA = oneOf(KEYFRAME_EASINGS, 'Easing of the keyframes it writes: linear, easeIn, easeOut or easeInOut.');
const DIRECTIONS = ['left', 'right', 'up', 'down'] as const;

const PLACEMENT_SCHEMA: JsonSchema = {
  anyOf: [
    obj({
      at: oneOf(['left', 'centerLeft', 'center', 'centerRight', 'right'], 'A slot across the stage.'),
      vertical: oneOf(['top', 'middle', 'ground'], 'Height on the stage; default ground (standing near the bottom).'),
    }, ['at'], 'A named slot across the stage, resolved for each orientation\'s stage.'),
    obj({
      relativeTo: str('The id of the sprite to stand beside.'),
      side: oneOf(['leftOf', 'rightOf'], 'Which side of that sprite.'),
      gap: oneOf(['close', 'normal', 'far'], 'Distance from that sprite; default normal.'),
    }, ['relativeTo', 'side'], 'Beside another sprite, at its height.'),
    obj({
      x: num(`Hinge x in % of the stage width (-50–150).`),
      y: num(`Hinge y in % of the stage height (-50–150).`),
    }, ['x', 'y'], 'Exact % of the stage.'),
  ],
};

// One object, never a union with a string: given [object | "none"], models
// send the object as JSON text and cannot be talked out of it.
const MOTION_SCHEMA = obj({
  type: oneOf(['none', 'fade', 'slide', 'scale', 'bounce', 'spin'], 'The effect; none appears or disappears at once.'),
  direction: oneOf(DIRECTIONS, 'Required for slide (the side it flies from or to); not allowed otherwise.'),
  offset: num(`Slide only: how far it travels, in % of the stage along the slide (${rangeText(RANGES.offset)}); default 110 (just off stage).`),
  duration: num(`Seconds (${rangeText(RANGES.motionDuration)}); default depends on the type.`),
  ease: KEY_EASE_SCHEMA,
}, ['type']);

export const TRANSITION_SCHEMA = obj({
  type: oneOf(['none', 'fade', 'slide', 'zoom', 'wipe'], 'The effect; none removes it.'),
  direction: oneOf(DIRECTIONS, 'Required for slide and wipe; not allowed otherwise.'),
  duration: num(`Seconds (${rangeText(RANGES.transitionDuration)}); default 0.5.`),
  ease: EASE_SCHEMA,
}, ['type']);

const STATE_SCHEMA = obj({
  name: str('State (expression) name, e.g. neutral, talking.'),
  mediaId: int('Image media id for this state. Omit when no picture fits yet: the state saves without one and plays the default state\'s picture or a labelled placeholder.'),
  playback: { anyOf: [PLAYBACK_ARG_SCHEMA, bool('false removes the playback: the sheet loops at its own speed.')] },
}, ['name']);

const ORIENTATION_SCHEMA = oneOf(ORIENTATIONS, 'Only this orientation (portrait phone, landscape phone or desktop): writes its own override and leaves the others as they play. Omit to change the base composition every orientation follows.');

const DEPTH_PROPS = {
  inFrontOf: str('Put it directly in front of this sprite id. Not with behind.'),
  behind: str('Put it directly behind this sprite id. Not with inFrontOf.'),
};

const SIZE_SCHEMA = obj({
  width: num(`Box width in canvas px (${rangeText(RANGES.size)}); rounded to whole px.`),
  height: num(`Box height in canvas px (${rangeText(RANGES.size)}); rounded to whole px.`),
}, ['width', 'height'], 'The sprite\'s box in canvas px, one value for every orientation (the picture is fitted inside it, never stretched). Its size on stage is this box times its scale.');

// ─── Helpers ────────────────────────────────────────────────────────────────

type Errors = OperationError[];
const fail = (errors: Errors): OperationResult => ({ ok: false, errors });
const e = (code: OperationError['code'], message: string, path?: string): OperationError =>
  (path ? { code, message, path } : { code, message });

function inRange(errors: Errors, value: number | undefined, range: readonly [number, number], path: string, unit = '') {
  if (value === undefined) return;
  if (value < range[0] || value > range[1]) {
    errors.push(e('out_of_range', `${path} must be ${range[0]}–${range[1]}${unit}`, path));
  }
}

function checkMedia(errors: Errors, ctx: OperationContext, mediaId: number | undefined, path: string) {
  if (mediaId === undefined || !ctx.media) return;
  if (!ctx.media.has(mediaId)) errors.push(e('unknown_media', `media ${mediaId} is not in the library`, path));
}

function checkName(errors: Errors, name: string | undefined, path: string) {
  if (name === undefined) return;
  if (name.trim() === '') errors.push(e('invalid_args', `${path} must not be empty`, path));
  else if (name.trim().length > RANGES.name[1]) errors.push(e('out_of_range', `${path} must be at most ${RANGES.name[1]} characters`, path));
}

function needOne(errors: Errors, args: object, keys: string[]) {
  if (!keys.some((k) => (args as Record<string, unknown>)[k] !== undefined)) {
    errors.push(e('invalid_args', `give at least one of ${keys.join(', ')}`));
  }
}

const spriteById = (doc: SceneDocument, id: string) => doc.sprites.find((s) => s.id === id);

function checkSpriteRef(errors: Errors, doc: SceneDocument, id: string | undefined, path: string) {
  if (id !== undefined && !spriteById(doc, id)) errors.push(e('not_found', `no sprite ${id}`, path));
}

/** A copy of the sprite whose `layout` and per-orientation entries the caller may mutate. */
function ownSprite(sprite: Sprite): Sprite {
  const layout = { ...sprite.layout };
  for (const o of ORIENTATIONS) if (layout[o]) layout[o] = { ...layout[o]! };
  return { ...sprite, layout };
}

const replaceById = <T extends { id: string }>(list: T[], item: T): T[] => list.map((x) => (x.id === item.id ? item : x));

const basisOf = (sprite: Sprite): Orientation => getBasisOrientation(sprite.layout) ?? 'portrait';
type Entry = OrientationLayout;
const storedPos = (entry: Entry | undefined) => entry?.position as Partial<Position> | undefined;
/** A stored position that an author (or a scoped operation) wrote: x or y stored, not marked derived. */
const isAuthoredPos = (entry: Entry | undefined) => {
  const p = storedPos(entry);
  return !!p && (p.x !== undefined || p.y !== undefined) && !isDerived(entry, 'position');
};

/**
 * The base placement (D-3, D-4): the basis position, and in each other
 * orientation the position resolved for its stage, marked `derived`, unless
 * that orientation holds an authored position, which it keeps (a one-axis
 * authored position gets the other axis from the resolved value).
 */
function writePlacement(sprite: Sprite, placement: Placement, doc: SceneDocument): void {
  const basis = basisOf(sprite);
  sprite.layout.basis = basis;
  const basisAnchor = sprite.layout[basis]?.position?.anchor ?? 'center';
  for (const o of ORIENTATIONS) {
    const { x, y } = resolvePlacement(placement, doc, sprite.id, o);
    const entry = sprite.layout[o];
    const anchor = entry?.position?.anchor ?? basisAnchor;
    if (o === basis) {
      const rest = withDerived(entry ?? ({} as Partial<Entry>), 'position', false);
      sprite.layout[o] = { ...rest, position: { anchor, x, y, unit: '%' } } as Entry;
    } else if (isAuthoredPos(entry)) {
      const p = storedPos(entry)!;
      if (p.x === undefined || p.y === undefined) {
        sprite.layout[o] = { ...entry!, position: { ...p, x: p.x ?? x, y: p.y ?? y } as Position };
      }
    } else {
      sprite.layout[o] = withDerived({ ...entry, position: { anchor, x, y, unit: '%' } } as Entry, 'position', true);
    }
  }
}

/**
 * A scoped placement (D-3, D-7). In a non-basis orientation: exactly the
 * editor's write in that orientation's view (`setPositionField`, which drops
 * the `derived` mark). In the basis: first every other orientation that
 * would otherwise move keeps what it plays now (a missing position stored
 * and marked `derived`, a missing axis of an authored one filled), then the
 * basis position is written.
 */
function writeScopedPlacement(sprite: Sprite, placement: Placement, doc: SceneDocument, o: Orientation): void {
  const basis = basisOf(sprite);
  const { x, y } = resolvePlacement(placement, doc, sprite.id, o);
  let layout: ResponsiveLayout = { ...sprite.layout, basis };
  if (o === basis) {
    for (const other of ORIENTATIONS) {
      if (other === basis) continue;
      const entry = layout[other];
      const p = storedPos(entry);
      const hasX = p?.x !== undefined;
      const hasY = p?.y !== undefined;
      if (hasX && hasY) continue;
      const now = getResponsivePosition(layout, other);
      if ((hasX || now.x === x) && (hasY || now.y === y)) continue;
      if (hasX || hasY) {
        layout = { ...layout, [other]: { ...entry!, position: { ...p, x: p!.x ?? now.x, y: p!.y ?? now.y } as Position } };
      } else {
        layout = { ...layout, [other]: withDerived({ ...entry, position: { anchor: now.anchor, x: now.x, y: now.y, unit: '%' } } as Entry, 'position', true) };
      }
    }
  }
  layout = setPositionField(layout, o, 'x', x);
  layout = setPositionField(layout, o, 'y', y);
  sprite.layout = layout;
}

/**
 * A scale or rotation write (D-3, D-4, D-7): the base value, or one
 * orientation's. Without `o`, each orientation whose value of `field` is
 * marked derived follows the new base again (its override and part removed);
 * an authored one is kept. In the basis, every other orientation that would
 * otherwise change keeps the value it plays now, marked derived. In a
 * non-basis `o`, the editor's write (`setOrientationTransform`, which drops
 * that part).
 */
function writeTransformField(sprite: Sprite, field: 'scale' | 'rotation', value: number, o: Orientation | undefined): void {
  const basis = basisOf(sprite);
  let layout = sprite.layout;
  for (const other of ORIENTATIONS) {
    if (other === basis) continue;
    if (o === undefined && isDerived(layout[other], field)) {
      layout = removeFieldOverride(layout, other, field);
    } else if (o === basis && layout[other]?.transform?.[field] === undefined) {
      const resolved = getResponsiveTransform(layout, other, sprite.transform);
      const now = field === 'scale' ? resolved.scale ?? 1 : resolved.rotation ?? 0;
      if (now === value) continue;
      layout = setOrientationTransform(layout, other, { [field]: now });
      layout = { ...layout, [other]: withDerived(layout[other]!, field, true) };
    }
  }
  sprite.layout = setOrientationTransform(layout, o ?? basis, { [field]: value });
}

/** A valid natural image size, or undefined. */
function knownSize(ctx: OperationContext, mediaId: number | undefined): SpriteSize | undefined {
  if (!mediaId || !ctx.imageSizes) return undefined;
  const n = ctx.imageSizes.get(mediaId);
  return n && n.width > 0 && n.height > 0 && Number.isFinite(n.width) && Number.isFinite(n.height) ? n : undefined;
}

/**
 * Spec 005's picture rule for a sprite without a stored size (D-19): the box
 * from the first picture whose natural size `ctx.imageSizes` knows, the
 * default state's first, then in order; undefined when none is known.
 */
function sizeFromPictures(ctx: OperationContext, states: readonly StateArg[], defaultState: string): SpriteSize | undefined {
  const first = states.find((st) => st.name.trim() === defaultState);
  const ordered = first ? [first, ...states.filter((st) => st !== first)] : states;
  for (const st of ordered) {
    const natural = knownSize(ctx, st.mediaId);
    if (natural) return sizeFromImage(natural);
  }
  return undefined;
}

const roundSize = (size: SpriteSize): SpriteSize => ({ width: Math.round(size.width), height: Math.round(size.height) });

function checkSize(errors: Errors, size: SpriteSize | undefined) {
  if (!size) return;
  inRange(errors, size.width, RANGES.size, 'size.width', ' px');
  inRange(errors, size.height, RANGES.size, 'size.height', ' px');
}

// ─── Keyframes in the document (D-1) ────────────────────────────────────────

/** The sprite's keyframe track in the document (`[]` when it has none). */
export const trackOf = (doc: SceneDocument, spriteId: string): Keyframe[] =>
  doc.keyframes?.find((t) => t.trackId === spriteId)?.keyframes ?? [];

/** The document with the sprite's track replaced by `keyframes` (an empty track is removed). */
export function withTrack(doc: SceneDocument, spriteId: string, keyframes: Keyframe[]): SceneDocument {
  const tracks = doc.keyframes ?? [];
  const at = tracks.findIndex((t) => t.trackId === spriteId);
  if (keyframes.length === 0) {
    return at === -1 ? doc : { ...doc, keyframes: tracks.filter((_, i) => i !== at) };
  }
  const track = { trackId: spriteId, keyframes };
  return { ...doc, keyframes: at === -1 ? [...tracks, track] : tracks.map((t, i) => (i === at ? track : t)) };
}

/**
 * The key ids an operation writes for a sprite, derived from the document
 * rather than drawn from `ctx.newId` (so replay needs no record): each call
 * gives `kf_<spriteId>_<n>` with the smallest `n` ≥ 1 that no keyframe in the
 * document, nor an id given before, uses. Spec 036's keyframe operations
 * share this rule.
 */
export function motionKeyframeId(doc: SceneDocument, spriteId: string): NewKeyframeId {
  const taken = new Set<string>();
  for (const t of doc.keyframes ?? []) for (const k of t.keyframes) taken.add(k.id);
  let n = 0;
  return () => {
    let id: string;
    do id = `kf_${spriteId}_${++n}`; while (taken.has(id));
    taken.add(id);
    return id;
  };
}

export const scopeText = (o: Orientation | undefined) => (o ? ` in ${o} only` : '');

function checkPlacement(errors: Errors, doc: SceneDocument, placement: Placement | undefined, selfId: string | undefined) {
  if (!placement) return;
  if ('relativeTo' in placement) {
    if (placement.relativeTo === selfId) errors.push(e('invalid_args', 'a sprite cannot be placed relative to itself', 'placement.relativeTo'));
    else checkSpriteRef(errors, doc, placement.relativeTo, 'placement.relativeTo');
  }
  if ('x' in placement) {
    inRange(errors, placement.x, [-50, 150], 'placement.x', '%');
    inRange(errors, placement.y, [-50, 150], 'placement.y', '%');
  }
}

function checkDepth(errors: Errors, doc: SceneDocument, args: { inFrontOf?: string; behind?: string }, selfId: string | undefined) {
  if (args.inFrontOf !== undefined && args.behind !== undefined) {
    errors.push(e('invalid_args', 'give inFrontOf or behind, not both', 'behind'));
    return;
  }
  for (const key of ['inFrontOf', 'behind'] as const) {
    const ref = args[key];
    if (ref === undefined) continue;
    if (ref === selfId) errors.push(e('invalid_args', 'a sprite cannot be in front of or behind itself', key));
    else checkSpriteRef(errors, doc, ref, key);
  }
}

function applyDepth(sprites: Sprite[], spriteId: string, args: { inFrontOf?: string; behind?: string }): Sprite[] {
  const ref = args.inFrontOf ?? args.behind;
  if (ref === undefined) return sprites;
  const z = depthZIndices(sprites, spriteId, ref, args.inFrontOf !== undefined ? 'inFrontOf' : 'behind');
  return sprites.map((s) => (z.get(s.id) === undefined || z.get(s.id) === s.zIndex ? s : { ...s, zIndex: z.get(s.id)! }));
}

/**
 * Absolute depth (D-18): the sprite at back-to-front rank `layer` (1 =
 * furthest back) and every zIndex renumbered 1..n in the new order. The same
 * array when the sprite already holds that rank and the values are distinct.
 */
function applyLayer(sprites: Sprite[], spriteId: string, layer: number): Sprite[] {
  const ordered = backToFront(sprites);
  const self = ordered.find((s) => s.id === spriteId)!;
  const distinct = new Set(sprites.map((s) => s.zIndex ?? 0)).size === sprites.length;
  if (distinct && ordered.indexOf(self) === layer - 1) return sprites;
  const rest = ordered.filter((s) => s !== self);
  rest.splice(layer - 1, 0, self);
  const z = new Map(rest.map((s, i) => [s.id, i + 1]));
  return sprites.map((s) => (s.zIndex === z.get(s.id) ? s : { ...s, zIndex: z.get(s.id)! }));
}

function placementText(p: Placement | undefined): string {
  if (!p) return '';
  if ('at' in p) return ` at ${p.at}${p.vertical && p.vertical !== 'ground' ? ` ${p.vertical}` : ''}`;
  if ('relativeTo' in p) return ` ${p.side === 'leftOf' ? 'left of' : 'right of'} ${p.relativeTo}`;
  return ` at ${p.x},${p.y}%`;
}

const depthText = (a: { inFrontOf?: string; behind?: string }) =>
  a.inFrontOf ? ` in front of ${a.inFrontOf}` : a.behind ? ` behind ${a.behind}` : '';

function checkEase(errors: Errors, ease: Ease | undefined, path: string) {
  if (ease !== undefined && !EASES.includes(ease)) errors.push(e('invalid_args', `${path} must be one of ${EASES.join(', ')}`, path));
}

function checkMotion(errors: Errors, m: Motion | undefined, path: string) {
  if (m === undefined) return;
  if (m.type === 'slide' && !m.direction) errors.push(e('invalid_args', `${path}.direction is required for slide`, `${path}.direction`));
  if (m.type !== 'slide' && m.direction) errors.push(e('invalid_args', `${path}.direction is only for slide`, `${path}.direction`));
  if (m.type !== 'slide' && m.offset !== undefined) errors.push(e('invalid_args', `${path}.offset is only for slide`, `${path}.offset`));
  inRange(errors, m.offset, RANGES.offset, `${path}.offset`, ' %');
  inRange(errors, m.duration, RANGES.motionDuration, `${path}.duration`, ' s');
  if (m.ease !== undefined && !(KEYFRAME_EASINGS as readonly string[]).includes(m.ease)) {
    errors.push(e('invalid_args', `${path}.ease must be one of ${KEYFRAME_EASINGS.join(', ')}`, `${path}.ease`));
  }
}

/** A Motion as 016's `Animation` (seconds → ms; the keyframe easing as it is). */
function toAnimation(m: Motion): Animation {
  if (m.type === 'none') return { type: 'none', duration: 0 };
  const a: Animation = {
    type: m.type,
    duration: m.duration !== undefined ? secToMs(m.duration) : DEFAULT_ANIMATION_DURATION_MS[m.type],
  };
  if (m.ease) a.ease = m.ease as KeyframeEasing;
  if (m.type === 'slide') {
    a.direction = m.direction;
    if (m.offset !== undefined) a.offset = m.offset;
  }
  return a;
}

const motionText = (m: Motion) =>
  m.type === 'none' ? 'none' : `${m.type}${m.direction ? ` ${m.direction}` : ''} ${m.duration !== undefined ? fmtS(m.duration) : fmtMs(DEFAULT_ANIMATION_DURATION_MS[m.type])}`;

export function checkTransition(errors: Errors, t: SceneTransitionArg | undefined, path: string) {
  if (t === undefined) return;
  const directed = t.type === 'slide' || t.type === 'wipe';
  if (directed && !t.direction) errors.push(e('invalid_args', `${path}.direction is required for ${t.type}`, `${path}.direction`));
  if (!directed && t.direction) errors.push(e('invalid_args', `${path}.direction is only for slide and wipe`, `${path}.direction`));
  inRange(errors, t.duration, RANGES.transitionDuration, `${path}.duration`, ' s');
  checkEase(errors, t.ease, `${path}.ease`);
}

/** Default scene transition length (ms), as the Scene panel's default. */
export const DEFAULT_TRANSITION_MS = 500;

export function toTransition(t: SceneTransitionArg): Transition {
  if (t.type === 'none') return { type: 'none', duration: 0 };
  const out: Transition = { type: t.type, duration: t.duration !== undefined ? secToMs(t.duration) : DEFAULT_TRANSITION_MS };
  if (t.ease) out.ease = EASE_TO_GSAP[t.ease];
  if (t.direction) out.direction = t.direction;
  return out;
}

/** The scene's play length (ms) as the player plays it (spec 012), its keyframes included. */
export function documentPlayLength(doc: SceneDocument): number {
  return toPlayableScene({ ...doc, keyframes: doc.keyframes ?? [] }).duration;
}

function checkStates(errors: Errors, ctx: OperationContext, states: StateArg[], path: string) {
  const seen = new Set<string>();
  states.forEach((s, i) => {
    const name = s.name.trim();
    if (!name) errors.push(e('invalid_args', `${path}[${i}].name must not be empty`, `${path}[${i}].name`));
    else if (seen.has(name)) errors.push(e('invalid_args', `${path}[${i}].name "${name}" is a duplicate`, `${path}[${i}].name`));
    seen.add(name);
    checkMedia(errors, ctx, s.mediaId, `${path}[${i}].mediaId`);
    if (s.playback === true) errors.push(e('invalid_args', `${path}[${i}].playback must be an object, or false to remove it`, `${path}[${i}].playback`));
  });
}

/** Spec 035: each state's `playback`, as `validateStatePlayback` (spec 033). */
function checkPlaybacks(errors: Errors, states: StateArg[], path: string) {
  states.forEach((s, i) => {
    if (!s.playback || typeof s.playback !== 'object') return;
    const reason = validateStatePlayback(playbackFromArg(s.playback));
    if (reason) errors.push(e('out_of_range', `${path}[${i}].${reason}`, `${path}[${i}].playback`));
  });
}

const newState = (name: string, mediaId: number | undefined, playback?: PlaybackArg | boolean): SpriteState => ({
  name, mediaId: mediaId ?? 0, animation: { type: 'none', duration: 1, repeat: 0 },
  ...(playback && typeof playback === 'object' ? { playback: playbackFromArg(playback) } : {}),
});

/**
 * A state as an operation takes it: without `mediaId` it has no picture yet
 * (`mediaId` 0, spec 026); `playback` sets how its spritesheet plays, and
 * `false` removes it (spec 035; JSON drops a null).
 */
export interface StateArg { name: string; mediaId?: number; playback?: PlaybackArg | boolean }

// ─── 1. addSprite ───────────────────────────────────────────────────────────

export interface AddSpriteArgs {
  name: string;
  states: StateArg[];
  defaultState?: string;
  size?: SpriteSize;
  placement?: Placement;
  inFrontOf?: string;
  behind?: string;
}

export const addSprite: SceneOperation<AddSpriteArgs> = {
  name: 'addSprite',
  description: 'Add a sprite (a character or object) with one or more image states (expressions); a state without mediaId has no picture yet. It is placed at the centre and in front of all others unless placement, inFrontOf or behind say otherwise. Its box is sized from its picture (longer side 160 px) unless size is given.',
  inputSchema: obj({
    name: str('Display name, e.g. Bob.'),
    states: arr(STATE_SCHEMA, 'The sprite\'s image states; at least one.', 1),
    defaultState: str('The state shown first; default the first state.'),
    size: SIZE_SCHEMA,
    placement: PLACEMENT_SCHEMA,
    ...DEPTH_PROPS,
  }, ['name', 'states']),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    checkName(errors, args.name, 'name');
    if (args.states.length === 0) errors.push(e('invalid_args', 'states needs at least one state', 'states'));
    checkStates(errors, ctx, args.states, 'states');
    checkPlaybacks(errors, args.states, 'states');
    if (args.defaultState !== undefined && !args.states.some((s) => s.name.trim() === args.defaultState!.trim())) {
      errors.push(e('invalid_args', `defaultState "${args.defaultState}" is not one of the states`, 'defaultState'));
    }
    checkSize(errors, args.size);
    checkPlacement(errors, doc, args.placement, undefined);
    checkDepth(errors, doc, args, undefined);
    if (errors.length) return fail(errors);

    const id = ctx.newId('sprite');
    const states = args.states.map((s) => newState(s.name.trim(), s.mediaId, s.playback));
    const front = doc.sprites.reduce((m, s) => Math.max(m, s.zIndex ?? 0), 0) + 1;
    const sprite: Sprite = {
      id,
      name: args.name.trim(),
      layout: { basis: 'portrait' },
      transform: { scale: 1, rotation: 0, flipX: false, flipY: false },
      zIndex: front,
      states,
      defaultState: args.defaultState?.trim() ?? states[0].name,
    };
    const size = args.size ? roundSize(args.size) : sizeFromPictures(ctx, args.states, sprite.defaultState);
    if (size) sprite.size = size;
    writePlacement(sprite, args.placement ?? { at: 'center' }, doc);
    const sprites = applyDepth([...doc.sprites, sprite], id, args);
    return {
      ok: true,
      doc: { ...doc, sprites },
      summary: `Added sprite ${sprite.name} (${id})${placementText(args.placement ?? { at: 'center' })}${depthText(args)}`,
      createdIds: [id],
    };
  },
};

// ─── 2. editSprite ──────────────────────────────────────────────────────────

export interface EditSpriteArgs {
  spriteId: string;
  name?: string;
  defaultState?: string;
  size?: SpriteSize;
  hinge?: { x: number; y: number };
  scale?: number;
  rotation?: number;
  /** The at-rest flips (spec 038 D-2): one value for every orientation. */
  flipX?: boolean;
  flipY?: boolean;
  orientation?: Orientation;
  setStates?: StateArg[];
  removeStates?: string[];
}

export const editSprite: SceneOperation<EditSpriteArgs> = {
  name: 'editSprite',
  description: 'Rename a sprite, set its box size, hinge, scale or rotation, mirror its picture (flipX, flipY), add or replace image states, remove states, or change the state shown first. A sprite\'s size on stage is its box (size) times its scale. A sprite always keeps at least one state. With orientation, only scale and rotation are allowed and they apply in that orientation only.',
  inputSchema: obj({
    spriteId: str('The sprite id.'),
    name: str('New display name.'),
    defaultState: str('The state shown first; must exist after the edit.'),
    size: SIZE_SCHEMA,
    hinge: obj({
      x: num(`0 = left edge, 0.5 = centre, 1 = right edge (${rangeText(RANGES.hinge)}).`),
      y: num(`0 = top edge, 0.5 = centre, 1 = bottom edge (${rangeText(RANGES.hinge)}).`),
    }, ['x', 'y'], 'The point of the box that sits at the sprite\'s position and that it scales and rotates about; one value for every orientation. The sprite stays where it is on stage.'),
    scale: num(`Factor on the box size, 1 = the box as sized (${rangeText(RANGES.scale)}). Without orientation: the base scale; orientations scaled on their own keep theirs.`),
    rotation: num(`Degrees clockwise (${rangeText(RANGES.rotation)}). Without orientation: the base rotation; orientations rotated on their own keep theirs.`),
    flipX: bool('true mirrors the picture left to right inside its box (a character drawn facing left then faces right); false shows it as drawn. One value for every orientation; to flip at a time, use flipSprite.'),
    flipY: bool('true mirrors the picture top to bottom inside its box; false shows it as drawn. One value for every orientation.'),
    orientation: ORIENTATION_SCHEMA,
    setStates: arr(STATE_SCHEMA, 'States to add (without mediaId: no picture yet), or to replace (same name) with a new image; an entry without mediaId leaves an existing state\'s image as it is.'),
    removeStates: arr(str('A state name.'), 'State names to remove.'),
  }, ['spriteId']),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    const sprite = spriteById(doc, args.spriteId);
    if (!sprite) return fail([e('not_found', `no sprite ${args.spriteId}`, 'spriteId')]);
    needOne(errors, args, ['name', 'defaultState', 'size', 'hinge', 'scale', 'rotation', 'flipX', 'flipY', 'setStates', 'removeStates']);
    if (args.orientation !== undefined) {
      for (const key of ['name', 'defaultState', 'size', 'hinge', 'flipX', 'flipY', 'setStates', 'removeStates'] as const) {
        if (args[key] !== undefined) errors.push(e('invalid_args', `${key} is one value for every orientation; with orientation only scale and rotation are allowed`, key));
      }
      if (args.scale === undefined && args.rotation === undefined) errors.push(e('invalid_args', 'with orientation, give scale or rotation', 'scale'));
    }
    checkName(errors, args.name, 'name');
    checkSize(errors, args.size);
    if (args.hinge) {
      inRange(errors, args.hinge.x, RANGES.hinge, 'hinge.x');
      inRange(errors, args.hinge.y, RANGES.hinge, 'hinge.y');
    }
    inRange(errors, args.scale, RANGES.scale, 'scale');
    inRange(errors, args.rotation, RANGES.rotation, 'rotation', ' degrees');
    for (const key of ['flipX', 'flipY'] as const) {
      if (args[key] !== undefined && typeof args[key] !== 'boolean') errors.push(e('invalid_args', `${key} must be true or false`, key));
    }
    const sets = args.setStates ?? [];
    checkStates(errors, ctx, sets, 'setStates');
    const removes = args.removeStates ?? [];
    removes.forEach((name, i) => {
      if (!sprite.states.some((s) => s.name === name)) errors.push(e('not_found', `no state "${name}" on ${sprite.name}`, `removeStates[${i}]`));
      if (sets.some((s) => s.name.trim() === name)) errors.push(e('invalid_args', `state "${name}" is both set and removed`, `removeStates[${i}]`));
    });
    if (errors.length) return fail(errors);

    let states = sprite.states.slice();
    for (const s of sets) {
      const name = s.name.trim();
      const at = states.findIndex((x) => x.name === name);
      if (at === -1) {
        states.push(newState(name, s.mediaId, s.playback));
        continue;
      }
      let next = states[at];
      if (s.mediaId !== undefined) next = { ...next, mediaId: s.mediaId };
      if (s.playback === false) {
        if (next.playback !== undefined) {
          const { playback: _removed, ...rest } = next;
          next = rest;
        }
      } else if (s.playback && typeof s.playback === 'object') {
        next = { ...next, playback: playbackFromArg(s.playback) };
      }
      states[at] = next;
    }
    states = states.filter((s) => !removes.includes(s.name));
    if (states.length === 0) return fail([e('invalid_args', 'a sprite must keep at least one state', 'removeStates')]);
    checkPlaybacks(errors, sets, 'setStates');
    if (errors.length) return fail(errors);
    let defaultState = sprite.defaultState;
    if (args.defaultState !== undefined) {
      if (!states.some((s) => s.name === args.defaultState)) {
        return fail([e('invalid_args', `defaultState "${args.defaultState}" is not one of the sprite's states`, 'defaultState')]);
      }
      defaultState = args.defaultState;
    } else if (!states.some((s) => s.name === defaultState)) {
      defaultState = states[0].name;
    }

    const next: Sprite = { ...sprite, states, defaultState };
    if (args.name !== undefined) next.name = args.name.trim();
    if (args.hinge) {
      // As the store's setSpriteHinge: the box stays where it is (compensated
      // with the sprite as it plays before this call).
      const oldHinge = sprite.hinge ?? { x: 0.5, y: 0.5 };
      const newHinge = { x: args.hinge.x, y: args.hinge.y };
      if (oldHinge.x !== newHinge.x || oldHinge.y !== newHinge.y) next.layout = compensateHinge(sprite, oldHinge, newHinge);
      next.hinge = newHinge;
    }
    // Spec 005's rule (D-19): an explicit size wins; a sizeless sprite takes
    // its box from the first picture this call gives it; a stored size stays.
    const size = args.size ? roundSize(args.size)
      : !sprite.size ? sizeFromPictures(ctx, sets.filter((st) => st.mediaId !== undefined), defaultState) : undefined;
    if (size) next.size = size;
    if (args.scale !== undefined) writeTransformField(next, 'scale', args.scale, args.orientation);
    if (args.rotation !== undefined) writeTransformField(next, 'rotation', args.rotation, args.orientation);
    if (args.flipX !== undefined) next.layout = setAtRestFlip(next.layout, 'x', args.flipX);
    if (args.flipY !== undefined) next.layout = setAtRestFlip(next.layout, 'y', args.flipY);
    const parts: string[] = [];
    if (args.name !== undefined) parts.push(`renamed to ${next.name}`);
    if (args.size) parts.push(`size ${size!.width}×${size!.height}`);
    if (args.hinge) parts.push(`hinge ${args.hinge.x},${args.hinge.y}`);
    if (args.scale !== undefined) parts.push(`scale ${args.scale}${scopeText(args.orientation)}`);
    if (args.rotation !== undefined) parts.push(`rotation ${args.rotation}°${scopeText(args.orientation)}`);
    if (args.flipX !== undefined) parts.push(`flipX ${args.flipX ? 'on' : 'off'}`);
    if (args.flipY !== undefined) parts.push(`flipY ${args.flipY ? 'on' : 'off'}`);
    if (sets.length) parts.push(`states set: ${sets.map((s) => s.name.trim()).join(', ')}`);
    if (removes.length) parts.push(`states removed: ${removes.join(', ')}`);
    if (defaultState !== sprite.defaultState) parts.push(`default state ${defaultState}`);
    return {
      ok: true,
      doc: { ...doc, sprites: replaceById(doc.sprites, next) },
      summary: `${sprite.name}: ${parts.join('; ') || 'unchanged'}`,
      createdIds: [],
    };
  },
};

// ─── 3. placeSprite ─────────────────────────────────────────────────────────

export interface PlaceSpriteArgs {
  spriteId: string;
  placement?: Placement;
  inFrontOf?: string;
  behind?: string;
  layer?: number;
  orientation?: Orientation;
}

export const placeSprite: SceneOperation<PlaceSpriteArgs> = {
  name: 'placeSprite',
  description: 'Move a sprite to a named slot, beside another sprite or to exact %, and/or change its depth: directly in front of or behind another sprite, or at an absolute layer. Without orientation it moves the base composition (orientations placed on their own keep their place); with orientation it moves the sprite in that orientation only (placement only, no depth).',
  inputSchema: obj({
    spriteId: str('The sprite id.'),
    placement: PLACEMENT_SCHEMA,
    ...DEPTH_PROPS,
    layer: int('Absolute depth: the back-to-front rank, 1 = furthest back, up to the number of sprites. Not with inFrontOf or behind.'),
    orientation: ORIENTATION_SCHEMA,
  }, ['spriteId']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = spriteById(doc, args.spriteId);
    if (!sprite) return fail([e('not_found', `no sprite ${args.spriteId}`, 'spriteId')]);
    if (args.orientation !== undefined) {
      for (const key of ['inFrontOf', 'behind', 'layer'] as const) {
        if (args[key] !== undefined) errors.push(e('invalid_args', 'depth is one order in every orientation; not with orientation', key));
      }
      if (args.placement === undefined) errors.push(e('invalid_args', 'with orientation, give placement', 'placement'));
    } else {
      needOne(errors, args, ['placement', 'inFrontOf', 'behind', 'layer']);
      if (args.layer !== undefined && (args.inFrontOf !== undefined || args.behind !== undefined)) {
        errors.push(e('invalid_args', 'give layer, or inFrontOf / behind, not both', 'layer'));
      }
      inRange(errors, args.layer, [1, doc.sprites.length], 'layer');
    }
    checkPlacement(errors, doc, args.placement, sprite.id);
    checkDepth(errors, doc, args, sprite.id);
    if (errors.length) return fail(errors);

    let sprites = doc.sprites;
    if (args.placement) {
      const own = ownSprite(sprite);
      if (args.orientation) writeScopedPlacement(own, args.placement, doc, args.orientation);
      else writePlacement(own, args.placement, doc);
      sprites = replaceById(sprites, own);
    }
    sprites = applyDepth(sprites, sprite.id, args);
    if (args.layer !== undefined) sprites = applyLayer(sprites, sprite.id, args.layer);
    if (sprites === doc.sprites) {
      return { ok: true, doc, summary: `${sprite.name}: already at layer ${args.layer} of ${sprites.length}`, createdIds: [] };
    }
    const layerText = args.layer !== undefined ? ` to layer ${args.layer} of ${sprites.length}` : '';
    return {
      ok: true,
      doc: { ...doc, sprites },
      summary: `${sprite.name}: moved${placementText(args.placement).replace(/^ at /, ' to ')}${depthText(args)}${layerText}${scopeText(args.orientation)}`,
      createdIds: [],
    };
  },
};

// ─── 4. setSpriteMotion ─────────────────────────────────────────────────────

export interface SetSpriteMotionArgs {
  spriteId: string;
  enter?: Motion;
  exit?: Motion;
  delay?: number;
  hold?: number;
  orientation?: Orientation;
}

export const setSpriteMotion: SceneOperation<SetSpriteMotionArgs> = {
  name: 'setSpriteMotion',
  description: 'Give a sprite an entrance and/or exit (fade, slide, scale, bounce, spin, or none for an instant appearance or disappearance), written as ordinary keyframes: the entrance starts delay seconds into the scene, and the exit ends hold seconds after the entrance (without hold, at the scene end). It replaces an earlier entrance or exit over the same span. Without orientation every orientation plays it; with portrait or landscape, only that orientation.',
  inputSchema: obj({
    spriteId: str('The sprite id.'),
    enter: MOTION_SCHEMA,
    exit: MOTION_SCHEMA,
    delay: num(`Seconds after the scene starts before it enters (${rangeText(RANGES.delayHold)}); with enter or exit only.`),
    hold: num(`Seconds it stays fully visible between entrance and exit; 0 or absent = the exit ends at the scene end (${rangeText(RANGES.delayHold)}); with exit only.`),
    orientation: ORIENTATION_SCHEMA,
  }, ['spriteId']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = spriteById(doc, args.spriteId);
    if (!sprite) return fail([e('not_found', `no sprite ${args.spriteId}`, 'spriteId')]);
    if (args.enter === undefined && args.exit === undefined) {
      const key = args.delay !== undefined ? 'delay' : args.hold !== undefined ? 'hold' : undefined;
      errors.push(key
        ? e('invalid_args', `${key} times an entrance or exit; give enter or exit with it. To make a character appear or leave at a time, use showSprite or hideSprite`, key)
        : e('invalid_args', 'give at least one of enter, exit'));
    }
    checkMotion(errors, args.enter, 'enter');
    checkMotion(errors, args.exit, 'exit');
    inRange(errors, args.delay, RANGES.delayHold, 'delay', ' s');
    inRange(errors, args.hold, RANGES.delayHold, 'hold', ' s');
    if (errors.length) return fail(errors);

    // 016's MotionPreset: the entrance by its start, the exit by its end.
    const atMs = secToMs(args.delay ?? 0);
    const preset: MotionPreset = {};
    const enterAnim = args.enter !== undefined ? toAnimation(args.enter) : undefined;
    if (enterAnim) preset.enter = { animation: enterAnim, atMs };
    if (args.exit !== undefined) {
      const exitAnim = toAnimation(args.exit);
      const holdMs = secToMs(args.hold ?? 0);
      const endMs = holdMs > 0
        ? atMs + (enterAnim ? motionDurationMs(enterAnim) : 0) + holdMs + motionDurationMs(exitAnim)
        : documentPlayLength(doc);
      preset.exit = { animation: exitAnim, endMs };
    }
    const keyframes = applyMotionPreset(trackOf(doc, sprite.id), sprite, preset, {
      orientation: args.orientation,
      newId: motionKeyframeId(doc, sprite.id),
    });

    const o = args.orientation;
    const parts: string[] = [];
    if (args.enter !== undefined) parts.push(`enter ${motionText(args.enter)}`);
    if (args.delay !== undefined) {
      if (args.enter !== undefined) parts[parts.length - 1] += ` after ${fmtS(args.delay)}`;
      else parts.push(`delay ${fmtS(args.delay)}`);
    }
    if (args.hold !== undefined) parts.push(args.hold === 0 ? 'hold until the end' : `hold ${fmtS(args.hold)}`);
    if (args.exit !== undefined) parts.push(`exit ${motionText(args.exit)}`);
    return {
      ok: true,
      doc: withTrack(doc, sprite.id, keyframes),
      summary: `${sprite.name}: ${parts.join(', ')}${scopeText(o)}`,
      createdIds: [],
    };
  },
};

// ─── 5. setDialog ───────────────────────────────────────────────────────────

export type DialogTransitionArg = 'none' | 'typing' | 'fade';
/** A dialog line's transitions (spec 041 D-9). */
export const DIALOG_TRANSITIONS: readonly DialogTransitionArg[] = ['none', 'typing', 'fade'];

export interface SetDialogArgs {
  dialogId?: string;
  name?: string;
  position?: 'top' | 'bottom';
  startTime?: number;
  inTransition?: DialogTransitionArg;
  outTransition?: DialogTransitionArg;
  lines: { text: string; duration?: number; voice?: number; inTransition?: DialogTransitionArg; outTransition?: DialogTransitionArg }[];
}

const IN_TRANSITION_TEXT = 'typing types the text in (40 ms a character, 0.5–3 s; default), fade fades it in (1 s), none shows it at once';
const OUT_TRANSITION_TEXT = 'fade fades the text out (1 s; default), typing untypes it, none removes it at once';

/**
 * A dialog's line transition time in ms (spec 041): the sum of its lines'
 * in- and out-transitions (the panel's own fades are not counted).
 */
export function dialogTransitionMs(dialog: Dialog): number {
  let total = 0;
  for (const line of dialog.lines ?? []) {
    const { inTransition, outTransition } = lineTransitions(dialog, line);
    total += transitionDurationMs(inTransition, line.text) + transitionDurationMs(outTransition, line.text);
  }
  return total;
}

/** A line id derived from the dialog id, unused in `taken` (deterministic, so replay needs no recorded id). */
function lineId(dialogId: string, taken: Set<string>): string {
  let n = 1;
  while (taken.has(`${dialogId}_l${n}`)) n++;
  const id = `${dialogId}_l${n}`;
  taken.add(id);
  return id;
}

/** A voice's length in ms (spec 041): the media map's, else `ctx.mediaLengths`'s; undefined when unknown. */
export function voiceLengthMs(ctx: Pick<OperationContext, 'media' | 'mediaLengths'>, mediaId: number | undefined): number | undefined {
  if (!mediaId) return undefined;
  const fromMap = ctx.media?.get(mediaId)?.durationMs;
  if (isKnownVoiceLength(fromMap)) return fromMap;
  const fromLengths = ctx.mediaLengths?.get(mediaId);
  return isKnownVoiceLength(fromLengths) ? fromLengths : undefined;
}

/** `voice` (spec 041): 0 removes the voice; otherwise an audio item of the media map, when there is one. */
function checkVoice(errors: Errors, ctx: OperationContext, voice: number | undefined, path: string) {
  if (voice === undefined || voice === 0) return;
  if (!Number.isInteger(voice) || voice < 0) {
    errors.push(e('invalid_args', `${path} must be an audio media id, or 0 to remove the voice`, path));
    return;
  }
  if (!ctx.media) return;
  const m = ctx.media.get(voice);
  if (!m) errors.push(e('not_found', `media ${voice} is not in the library`, path));
  else if (m.type !== 'audio') errors.push(e('invalid_args', `media ${voice} is ${m.type || 'not audio'}; a voice must be audio`, path));
}

/** A transition argument (spec 041 D-9): `none`, `typing` or `fade`. */
function checkLineTransition(errors: Errors, value: unknown, path: string) {
  if (value === undefined) return;
  if (!DIALOG_TRANSITIONS.includes(value as DialogTransitionArg)) {
    errors.push(e('invalid_args', `${path} must be one of ${DIALOG_TRANSITIONS.join(', ')}`, path));
  }
}

export const setDialog: SceneOperation<SetDialogArgs> = {
  name: 'setDialog',
  description: 'Create a dialog (no dialogId) or replace an existing dialog\'s lines. Each line types its text in, holds it for its duration, then fades it out; lines play one after another from the dialog\'s start time. Give a line its voice (an audio mediaId) and leave out its duration, and it lasts as long as the voice plus a short pause, with the text typing in while the voice speaks; the out-transition plays after that. inTransition and outTransition set the transitions of every line, or of one line.',
  inputSchema: obj({
    dialogId: str('The dialog to change; omit to create one.'),
    name: str('Display name in the editor.'),
    position: oneOf(['top', 'bottom'], 'Where the dialog box sits; default bottom.'),
    startTime: num(`Seconds from the scene start when the dialog appears (${rangeText(RANGES.sceneTime)}).`),
    inTransition: oneOf(DIALOG_TRANSITIONS, `How each line\'s text appears unless the line sets its own: ${IN_TRANSITION_TEXT}.`),
    outTransition: oneOf(DIALOG_TRANSITIONS, `How each line\'s text goes unless the line sets its own: ${OUT_TRANSITION_TEXT}.`),
    lines: arr(obj({
      text: str(`The line\'s text (at most ${RANGES.lineText[1]} characters).`),
      duration: num(`Seconds the line holds after its in-transition (${rangeText(RANGES.lineDuration)}); default ${DEFAULT_LINE_DURATION_S}, or with a voice the voice length plus ${VOICE_PAUSE_S} minus the in-transition. Giving it fixes the line at that length.`),
      voice: int('The line\'s voice: an audio mediaId from listMedia; 0 removes it. Omit to keep the line\'s voice as it is.'),
      inTransition: oneOf(DIALOG_TRANSITIONS, `This line\'s own in-transition: ${IN_TRANSITION_TEXT}. Omit to keep the line\'s as it is.`),
      outTransition: oneOf(DIALOG_TRANSITIONS, `This line\'s own out-transition: ${OUT_TRANSITION_TEXT}. Omit to keep the line\'s as it is.`),
    }, ['text']), 'The lines, in order; they replace all existing lines.', 1),
  }, ['lines']),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    const existing = args.dialogId !== undefined ? doc.dialogs.find((d) => d.id === args.dialogId) : undefined;
    if (args.dialogId !== undefined && !existing) return fail([e('not_found', `no dialog ${args.dialogId}`, 'dialogId')]);
    checkName(errors, args.name, 'name');
    inRange(errors, args.startTime, RANGES.sceneTime, 'startTime', ' s');
    checkLineTransition(errors, args.inTransition, 'inTransition');
    checkLineTransition(errors, args.outTransition, 'outTransition');
    if (args.lines.length === 0) errors.push(e('invalid_args', 'lines needs at least one line', 'lines'));
    args.lines.forEach((l, i) => {
      if (l.text.length > RANGES.lineText[1]) errors.push(e('out_of_range', `lines[${i}].text must be at most ${RANGES.lineText[1]} characters`, `lines[${i}].text`));
      inRange(errors, l.duration, RANGES.lineDuration, `lines[${i}].duration`, ' s');
      checkVoice(errors, ctx, l.voice, `lines[${i}].voice`);
      checkLineTransition(errors, l.inTransition, `lines[${i}].inTransition`);
      checkLineTransition(errors, l.outTransition, `lines[${i}].outTransition`);
    });
    if (errors.length) return fail(errors);

    const createdIds: string[] = [];
    const id = existing ? existing.id : ctx.newId('dialog');
    if (!existing) createdIds.push(id);
    const old = existing?.lines ?? [];
    const taken = new Set(old.map((l) => l.id));
    const notes: string[] = [];
    // Text, voice, transitions and set durations first (spec 041 D-9)…
    const built: DialogLine[] = args.lines.map((l, i) => {
      const prev = old[i];
      const line: DialogLine = prev
        ? { ...prev, text: l.text }
        : { id: lineId(id, taken), text: l.text, duration: DEFAULT_LINE_DURATION_S };
      if (l.voice === 0) delete line.audioId;
      else if (l.voice !== undefined) line.audioId = l.voice;
      if (l.inTransition !== undefined) line.inTransition = l.inTransition;
      if (l.outTransition !== undefined) line.outTransition = l.outTransition;
      if (l.duration !== undefined) {
        line.duration = l.duration;
        line.durationSet = true;
      }
      return line;
    });
    const base: Dialog = existing
      ? { ...existing, lines: built }
      : { id, name: `Dialog ${doc.dialogs.length + 1}`, position: 'bottom', lines: built };
    if (args.inTransition !== undefined) base.inTransition = args.inTransition;
    if (args.outTransition !== undefined) base.outTransition = args.outTransition;
    // …then every voiced, unset line is fitted with its in-transition, whether or not the call named its voice.
    const lines = built.map((line, i) => {
      const given = args.lines[i];
      const voiceMs = voiceLengthMs(ctx, line.audioId);
      const named = given.voice !== undefined && given.voice !== 0 && given.duration === undefined;
      if (named && voiceMs === undefined) notes.push(`line ${i + 1} voice length unknown`);
      else if (named && line.durationSet) notes.push(`line ${i + 1} kept ${fmtS(line.duration ?? DEFAULT_LINE_DURATION_S)} (set)`);
      return fitLineToVoice(line, voiceMs, lineInMs(base, line));
    });
    const dialog: Dialog = { ...base, lines };
    if (args.name !== undefined) dialog.name = args.name.trim();
    if (args.position !== undefined) dialog.position = args.position;
    if (args.startTime !== undefined) dialog.startTime = secToMs(args.startTime);
    const dialogs = existing ? replaceById(doc.dialogs, dialog) : [...doc.dialogs, dialog];

    // A voice longer than its line is cut at the line's end (spec 007's voice cue).
    const timeline = computeDialogTimeline(dialog);
    lines.forEach((line, i) => {
      const voiceMs = voiceLengthMs(ctx, line.audioId);
      const t = timeline.lines[i];
      if (voiceMs !== undefined && t && voiceMs > t.endMs - t.startMs) {
        notes.push(`line ${i + 1} cuts its voice (${fmtMs(voiceMs)} voice, ${fmtS(line.duration ?? DEFAULT_LINE_DURATION_S)} line)`);
      }
    });
    const voiced = lines.filter((l) => l.audioId && l.audioId > 0).length;
    const at = args.startTime !== undefined ? ` at ${fmtS(args.startTime)}` : '';
    const transitionMs = dialogTransitionMs(dialog);
    const transitions = transitionMs > 0 ? ` (${fmtMs(transitionMs)} of line transitions)` : '';
    return {
      ok: true,
      doc: { ...doc, dialogs },
      summary: `${existing ? 'Dialog' : 'Added dialog'} ${dialog.name} (${id})${at}: ${lines.length} line${lines.length === 1 ? '' : 's'}`
        + `${voiced ? `, ${voiced} voiced` : ''}, ends ${fmtMs(timeline.endMs)}${transitions}${notes.map((n) => `; ${n}`).join('')}`,
      createdIds,
    };
  },
};

// ─── 6. setSound ────────────────────────────────────────────────────────────

export interface SetSoundArgs {
  soundId?: string;
  mediaId?: number;
  name?: string;
  type?: SoundType;
  startTime?: number;
  endTime?: number;
  volume?: number;
  pan?: number;
  loop?: boolean;
  fadeIn?: number;
  fadeOut?: number;
}

const SOUND_NAMES: Record<SoundType, string> = { bgm: 'Background Music', sfx: 'Sound Effect', voice: 'Voice' };

export const setSound: SceneOperation<SetSoundArgs> = {
  name: 'setSound',
  description: 'Add a sound (no soundId; mediaId required) or change one: its audio, type (bgm, sfx, voice), timing, volume, pan, loop and fades. Times are clamped to the scene as the editor clamps them.',
  inputSchema: obj({
    soundId: str('The sound to change; omit to add one.'),
    mediaId: int('Audio media id; required when adding.'),
    name: str('Display name.'),
    type: oneOf(['bgm', 'sfx', 'voice'], 'bgm = background music, sfx = effect, voice = voice-over; default sfx.'),
    startTime: num(`Seconds from the scene start (${rangeText(RANGES.sceneTime)}).`),
    endTime: num(`Seconds from the scene start when it stops; later than startTime (${rangeText(RANGES.sceneTime)}).`),
    volume: num(`0–1.`),
    pan: num(`-1 (left) to 1 (right), 0 = centre.`),
    loop: bool('Repeat until it stops.'),
    fadeIn: num(`Seconds (${rangeText(RANGES.fade)}).`),
    fadeOut: num(`Seconds (${rangeText(RANGES.fade)}).`),
  }),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    const existing = args.soundId !== undefined ? doc.sounds.find((s) => s.id === args.soundId) : undefined;
    if (args.soundId !== undefined && !existing) return fail([e('not_found', `no sound ${args.soundId}`, 'soundId')]);
    if (!existing && args.mediaId === undefined) errors.push(e('invalid_args', 'mediaId is required to add a sound', 'mediaId'));
    if (existing) needOne(errors, args, ['mediaId', 'name', 'type', 'startTime', 'endTime', 'volume', 'pan', 'loop', 'fadeIn', 'fadeOut']);
    checkMedia(errors, ctx, args.mediaId, 'mediaId');
    checkName(errors, args.name, 'name');
    inRange(errors, args.startTime, RANGES.sceneTime, 'startTime', ' s');
    inRange(errors, args.endTime, RANGES.sceneTime, 'endTime', ' s');
    inRange(errors, args.volume, RANGES.volume, 'volume');
    inRange(errors, args.pan, RANGES.pan, 'pan');
    inRange(errors, args.fadeIn, RANGES.fade, 'fadeIn', ' s');
    inRange(errors, args.fadeOut, RANGES.fade, 'fadeOut', ' s');
    if (errors.length) return fail(errors);

    const createdIds: string[] = [];
    let sound: Sound;
    if (existing) {
      sound = { ...existing };
      if (args.type !== undefined && args.type !== existing.type) Object.assign(sound, soundTypeChange(existing, args.type));
    } else {
      const type = args.type ?? 'sfx';
      const id = ctx.newId('sound');
      createdIds.push(id);
      sound = { id, name: SOUND_NAMES[type], mediaId: args.mediaId!, type, ...SOUND_TYPE_DEFAULTS[type], pan: 0 };
    }
    if (args.mediaId !== undefined) sound.mediaId = args.mediaId;
    if (args.name !== undefined) sound.name = args.name.trim();
    if (args.volume !== undefined) sound.volume = args.volume;
    if (args.pan !== undefined) sound.pan = args.pan;
    if (args.loop !== undefined) sound.loop = args.loop;
    const patch: Partial<Pick<Sound, 'startTime' | 'endTime' | 'fadeIn' | 'fadeOut'>> = {};
    if (args.startTime !== undefined) patch.startTime = secToMs(args.startTime);
    if (args.endTime !== undefined) patch.endTime = secToMs(args.endTime);
    if (args.fadeIn !== undefined) patch.fadeIn = secToMs(args.fadeIn);
    if (args.fadeOut !== undefined) patch.fadeOut = secToMs(args.fadeOut);
    if (Object.keys(patch).length > 0) {
      // The editor's one clamp (spec 007), against the play length the player plays.
      const next = clampSoundTiming(sound, patch, documentPlayLength(doc));
      sound.startTime = next.startTime;
      if (next.endTime === undefined) delete sound.endTime;
      else sound.endTime = next.endTime;
      sound.fadeIn = next.fadeIn;
      sound.fadeOut = next.fadeOut;
    }
    const sounds = existing ? replaceById(doc.sounds, sound) : [...doc.sounds, sound];
    const window = sound.startTime !== undefined || sound.endTime !== undefined
      ? ` ${fmtMs(sound.startTime)}–${sound.endTime !== undefined ? fmtMs(sound.endTime) : 'end'}` : '';
    return {
      ok: true,
      doc: { ...doc, sounds },
      summary: `${existing ? 'Sound' : 'Added sound'} ${sound.name} (${sound.id}): ${sound.type}${window}`,
      createdIds,
    };
  },
};

// ─── 7. setBackground ───────────────────────────────────────────────────────

export interface SetBackgroundArgs {
  mediaId?: number;
  color?: string;
  name?: string;
}

/** A background name: 1–120 characters after trimming (spec 026). */
export const BACKGROUND_NAME_MAX = 120;

export const setBackground: SceneOperation<SetBackgroundArgs> = {
  name: 'setBackground',
  description: 'Set the scene background image, colour and/or name (the place it shows). Name a background no picture fits yet: it plays as a labelled placeholder. Parallax layers are left as they are.',
  inputSchema: obj({
    mediaId: int('Background image media id.'),
    color: str('CSS colour, e.g. #1a1a2e.'),
    name: str(`The place it shows, e.g. Office (1–${BACKGROUND_NAME_MAX} characters).`),
  }),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    needOne(errors, args, ['mediaId', 'color', 'name']);
    checkMedia(errors, ctx, args.mediaId, 'mediaId');
    if (args.color !== undefined && (args.color.trim() === '' || args.color.length > 64)) {
      errors.push(e('invalid_args', 'color must be a CSS colour', 'color'));
    }
    if (args.name !== undefined) {
      const n = args.name.trim();
      if (n.length < 1 || n.length > BACKGROUND_NAME_MAX) errors.push(e('out_of_range', `name must be 1–${BACKGROUND_NAME_MAX} characters`, 'name'));
    }
    if (errors.length) return fail(errors);
    let background = { ...doc.background };
    // With states (spec 039), the picture is the default state's, written through the one writer.
    const def = backgroundDefaultState(background);
    if (args.mediaId !== undefined && def !== undefined) {
      const states = backgroundStates(background).map((s) => (s.name === def ? { ...s, mediaId: args.mediaId } : s));
      const written = writeBackgroundStates(background, states, def);
      if (written.ok === false) return fail([e('invalid_args', written.error.message, 'mediaId')]);
      background = written.background;
    } else if (args.mediaId !== undefined) background.baseMediaId = args.mediaId;
    if (args.color !== undefined) background.color = args.color.trim();
    if (args.name !== undefined) background.name = args.name.trim();
    const parts = [
      args.name !== undefined ? `"${background.name}"` : '',
      args.mediaId !== undefined ? `image ${args.mediaId}${def !== undefined ? ` (state ${def})` : ''}` : '',
      args.color !== undefined ? `colour ${background.color}` : '',
    ].filter(Boolean);
    return { ok: true, doc: { ...doc, background }, summary: `Background: ${parts.join(', ')}`, createdIds: [] };
  },
};

// ─── 8. setSceneTransitions ─────────────────────────────────────────────────

export interface SetSceneTransitionsArgs {
  enter?: SceneTransitionArg;
  exit?: SceneTransitionArg;
}

export const setSceneTransitions: SceneOperation<SetSceneTransitionsArgs> = {
  name: 'setSceneTransitions',
  description: 'Set how the whole scene transitions in at its start and out at its end (fade, slide, zoom, wipe or none).',
  inputSchema: obj({ enter: TRANSITION_SCHEMA, exit: TRANSITION_SCHEMA }),
  apply(doc, args) {
    const errors: Errors = [];
    needOne(errors, args, ['enter', 'exit']);
    checkTransition(errors, args.enter, 'enter');
    checkTransition(errors, args.exit, 'exit');
    if (errors.length) return fail(errors);
    const next = { ...doc };
    const parts: string[] = [];
    const text = (t: SceneTransitionArg) =>
      t.type === 'none' ? 'none' : `${t.type}${t.direction ? ` ${t.direction}` : ''} ${fmtMs(toTransition(t).duration)}`;
    if (args.enter !== undefined) { next.enterTransition = toTransition(args.enter); parts.push(`enter ${text(args.enter)}`); }
    if (args.exit !== undefined) { next.exitTransition = toTransition(args.exit); parts.push(`exit ${text(args.exit)}`); }
    return { ok: true, doc: next, summary: `Scene transitions: ${parts.join(', ')}`, createdIds: [] };
  },
};

// ─── 8b. setSceneLength (spec 041) ──────────────────────────────────────────

export interface SetSceneLengthArgs { seconds: number }

/** The held lengths `setSceneLength` takes, seconds (0 = follows content). */
export const SCENE_LENGTH_RANGE = [1, 3600] as const;

/**
 * Whether the scene is held (spec 041 D-7): its content ends before its stored
 * `duration`. Play length is the longer of the two, so a stored length at or
 * under the content end holds nothing.
 */
export function isHeldScene(doc: SceneDocument): boolean {
  if (typeof doc.duration !== 'number' || !Number.isFinite(doc.duration) || doc.duration <= 0) return false;
  return documentContentEnd(doc) < doc.duration;
}

/** Where the scene's content ends (ms): its play length were it not held (spec 041). */
export function documentContentEnd(doc: SceneDocument): number {
  return documentPlayLength({ ...doc, duration: 0 });
}

export const setSceneLength: SceneOperation<SetSceneLengthArgs> = {
  name: 'setSceneLength',
  description: 'Set how long the scene plays. A scene plays until its last line, timed sound or movement ends; seconds holds it longer (say, a pause after the last line), and 0 lets it follow its content again. A scene is never shorter than its content.',
  inputSchema: obj({
    seconds: num(`The scene\'s length in seconds (${rangeText(SCENE_LENGTH_RANGE)}), at least where its content ends; 0 = follow the content.`),
  }, ['seconds']),
  apply(doc, args) {
    const seconds = args.seconds;
    if (!Number.isFinite(seconds) || (seconds !== 0 && (seconds < SCENE_LENGTH_RANGE[0] || seconds > SCENE_LENGTH_RANGE[1]))) {
      return fail([e('out_of_range', `seconds must be 0 or ${rangeText(SCENE_LENGTH_RANGE)}`, 'seconds')]);
    }
    const contentEnd = documentContentEnd(doc);
    if (seconds === 0) {
      return { ok: true, doc: { ...doc, duration: 0 }, summary: `Scene follows its content (${fmtMs(contentEnd)})`, createdIds: [] };
    }
    const held = secToMs(seconds);
    if (held < contentEnd) {
      return fail([e('invalid_args', `content runs to ${fmtMs(contentEnd)}; a scene can't be shorter than its content`, 'seconds')]);
    }
    return { ok: true, doc: { ...doc, duration: held }, summary: `Scene plays ${fmtMs(held)} (held; content ends ${fmtMs(contentEnd)})`, createdIds: [] };
  },
};

// ─── 9. setChoices ──────────────────────────────────────────────────────────

export interface SetChoicesArgs {
  options: { choiceId?: string; text: string; mediaId?: number }[];
  settings?: ChoiceSettings;
}

export const setChoices: SceneOperation<SetChoicesArgs> = {
  name: 'setChoices',
  description: 'Replace the choices shown at the end of the scene (0–4 options; [] removes them) and optionally their settings. Keep an option\'s choiceId to keep its story connection; options without one are new.',
  inputSchema: obj({
    options: arr(obj({
      choiceId: str('An existing choice id to keep (its story connection stays).'),
      text: str(`Button label (at most ${CHOICE_TEXT_MAX} characters).`),
      mediaId: int('Optional image media id.'),
    }, ['text']), `The options in order, at most ${MAX_CHOICES}.`),
    settings: obj({
      layout: oneOf(['list', 'grid'], 'Stacked list or 2-column grid.'),
      position: oneOf(['bottom', 'center'], 'Where the panel appears.'),
      timer: int(`Seconds until the first option is chosen automatically (0–${CHOICE_TIMER_MAX_S}); 0 = no timer.`),
      shuffleOptions: bool('Shuffle the order on each play.'),
      showBeforeEnd: num(`Show the panel this many seconds before the scene ends (${rangeText(RANGES.showBeforeEnd)}).`),
    }),
  }, ['options']),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    const current = doc.choices ?? [];
    if (args.options.length > MAX_CHOICES) {
      errors.push(e('out_of_range', `options must have 0–${MAX_CHOICES} entries`, 'options'));
    }
    const kept = new Set<string>();
    args.options.forEach((o, i) => {
      if (o.text.length > CHOICE_TEXT_MAX) errors.push(e('out_of_range', `options[${i}].text must be at most ${CHOICE_TEXT_MAX} characters`, `options[${i}].text`));
      checkMedia(errors, ctx, o.mediaId, `options[${i}].mediaId`);
      if (o.choiceId === undefined) return;
      if (!current.some((c) => c.id === o.choiceId)) errors.push(e('not_found', `no choice ${o.choiceId}`, `options[${i}].choiceId`));
      else if (kept.has(o.choiceId)) errors.push(e('invalid_args', `choice ${o.choiceId} is given twice`, `options[${i}].choiceId`));
      kept.add(o.choiceId);
    });
    const s = args.settings;
    if (s?.timer !== undefined && (s.timer < 0 || s.timer > CHOICE_TIMER_MAX_S)) {
      errors.push(e('out_of_range', `settings.timer must be 0–${CHOICE_TIMER_MAX_S}`, 'settings.timer'));
    }
    inRange(errors, s?.showBeforeEnd, RANGES.showBeforeEnd, 'settings.showBeforeEnd', ' s');
    if (errors.length) return fail(errors);

    const createdIds: string[] = [];
    const choices: ChoiceOption[] = args.options.map((o) => {
      const prev = o.choiceId !== undefined ? current.find((c) => c.id === o.choiceId) : undefined;
      let id = prev?.id;
      if (!id) { id = ctx.newId('choice'); createdIds.push(id); }
      const c: ChoiceOption = { id, text: o.text };
      const mediaId = o.mediaId ?? prev?.mediaId;
      if (mediaId !== undefined) c.mediaId = mediaId;
      return c;
    });
    let choiceSettings = doc.choiceSettings;
    if (s && Object.keys(s).length > 0) choiceSettings = { ...(doc.choiceSettings ?? {}), ...s };
    else if (choices.length > 0 && !choiceSettings) choiceSettings = {};
    const shapeError = validateChoices(choices) ?? validateChoiceSettings(choiceSettings);
    if (shapeError) return fail([e('invalid_args', shapeError)]);

    const removed = current.filter((c) => !kept.has(c.id)).map((c) => c.id);
    const next: SceneDocument = { ...doc, choices };
    if (choiceSettings !== undefined) next.choiceSettings = choiceSettings;
    const list = choices.length ? choices.map((c) => `"${c.text}" (${c.id})`).join(', ') : 'none';
    const removedText = removed.length
      ? `; removed ${removed.join(', ')}: a story connection from ${removed.length === 1 ? 'it' : 'them'} is now dangling`
      : '';
    return { ok: true, doc: next, summary: `Choices: ${list}${removedText}`, createdIds };
  },
};

// ─── 10. removeElement ──────────────────────────────────────────────────────

export interface RemoveElementArgs {
  id: string;
}

export const removeElement: SceneOperation<RemoveElementArgs> = {
  name: 'removeElement',
  description: 'Remove a sprite (with its keyframes), dialog or sound by id.',
  inputSchema: obj({ id: str('The sprite, dialog or sound id.') }, ['id']),
  apply(doc, args) {
    const sprite = doc.sprites.find((s) => s.id === args.id);
    if (sprite) {
      // As the store's deleteSprite: the sprite's keyframe track goes with it.
      const next = withTrack({ ...doc, sprites: doc.sprites.filter((s) => s !== sprite) }, sprite.id, []);
      return { ok: true, doc: next, summary: `Removed sprite ${sprite.name} (${sprite.id})`, createdIds: [] };
    }
    const dialog = doc.dialogs.find((d) => d.id === args.id);
    if (dialog) return { ok: true, doc: { ...doc, dialogs: doc.dialogs.filter((d) => d !== dialog) }, summary: `Removed dialog ${dialog.name} (${dialog.id})`, createdIds: [] };
    const sound = doc.sounds.find((s) => s.id === args.id);
    if (sound) return { ok: true, doc: { ...doc, sounds: doc.sounds.filter((s) => s !== sound) }, summary: `Removed sound ${sound.name} (${sound.id})`, createdIds: [] };
    return fail([e('not_found', `no sprite, dialog or sound ${args.id}`, 'id')]);
  },
};

// ─── 11. renameScene ────────────────────────────────────────────────────────

export interface RenameSceneArgs {
  name: string;
}

export const renameScene: SceneOperation<RenameSceneArgs> = {
  name: 'renameScene',
  description: `Rename the scene (trimmed, at most ${SCENE_NAME_MAX_LENGTH} characters).`,
  inputSchema: obj({ name: str('The new scene name.') }, ['name']),
  apply(doc, args) {
    const name = normaliseSceneName(args.name);
    if (!name) return fail([e('invalid_args', 'name must not be empty', 'name')]);
    if (name === doc.name) return { ok: true, doc, summary: `Scene name unchanged (${name})`, createdIds: [] };
    return { ok: true, doc: { ...doc, name }, summary: `Renamed the scene to ${name}`, createdIds: [] };
  },
};

// ─── 12. resetSpriteOrientation ─────────────────────────────────────────────

export type ResetPart = 'position' | 'scale' | 'rotation' | 'motion';

export interface ResetSpriteOrientationArgs {
  spriteId: string;
  orientation: Orientation;
  parts?: ResetPart[];
}

const RESET_PARTS: readonly ResetPart[] = ['position', 'scale', 'rotation', 'motion'];

export const resetSpriteOrientation: SceneOperation<ResetSpriteOrientationArgs> = {
  name: 'resetSpriteOrientation',
  description: 'Put a sprite back on the base composition in one orientation: drop that orientation\'s own position, scale, rotation and/or motion (its keyframes of that orientation; all four by default), so it follows the base again. Not for the orientation the sprite was composed in.',
  inputSchema: obj({
    spriteId: str('The sprite id.'),
    orientation: oneOf(ORIENTATIONS, 'The orientation to put back on the base composition; not the sprite\'s own composing orientation.'),
    parts: arr(oneOf(RESET_PARTS, 'position, scale, rotation or motion (the keyframes of that orientation only).'), 'What to reset; default all four.', 1),
  }, ['spriteId', 'orientation']),
  apply(doc, args) {
    const sprite = spriteById(doc, args.spriteId);
    if (!sprite) return fail([e('not_found', `no sprite ${args.spriteId}`, 'spriteId')]);
    const o = args.orientation;
    const basis = basisOf(sprite);
    if (o === basis) {
      return fail([e('invalid_args', `${o} is ${sprite.name}'s base composition; move it with placeSprite, editSprite or setSpriteMotion without orientation`, 'orientation')]);
    }
    const parts = args.parts ?? RESET_PARTS;
    if (parts.length === 0) return fail([e('invalid_args', 'parts needs at least one part', 'parts')]);

    const own = ownSprite(sprite);
    const done: ResetPart[] = [];
    let keyed = doc;
    if (parts.includes('motion')) {
      // The orientation's tagged keys go, so it plays the untagged ones again (006).
      const track = trackOf(doc, sprite.id);
      const kept = track.filter((k) => k.orientation !== o);
      if (kept.length !== track.length) {
        keyed = withTrack(doc, sprite.id, kept);
        done.push('motion');
      }
    }
    if (parts.includes('scale') && own.layout[o]?.transform?.scale !== undefined) {
      // As the PropertiesPanel's reset (↺) of the Scale field.
      own.layout = removeFieldOverride(own.layout, o, 'scale');
      done.push('scale');
    }
    if (parts.includes('rotation') && own.layout[o]?.transform?.rotation !== undefined) {
      // As the PropertiesPanel's reset of the Rotation field.
      own.layout = removeFieldOverride(own.layout, o, 'rotation');
      done.push('rotation');
    }
    if (parts.includes('position') && isAuthoredPos(own.layout[o])) {
      const base = getResponsivePosition(own.layout, basis);
      const { x, y } = derivePosition(base, basis, o);
      const anchor = own.layout[o]!.position?.anchor ?? base.anchor;
      own.layout = { ...own.layout, [o]: withDerived({ ...own.layout[o]!, position: { anchor, x, y, unit: '%' } }, 'position', true) };
      done.push('position');
    }
    if (done.length === 0) {
      return { ok: true, doc, summary: `${sprite.name}: ${o} already follows the base`, createdIds: [] };
    }
    const ordered = RESET_PARTS.filter((p) => done.includes(p));
    const layoutChanged = done.some((p) => p !== 'motion');
    return {
      ok: true,
      doc: layoutChanged ? { ...keyed, sprites: replaceById(doc.sprites, own) } : keyed,
      summary: `${sprite.name}: ${o} follows the base again (${ordered.join(', ')} reset)`,
      createdIds: [],
    };
  },
};

// ─── 13. setLineExpression (spec 026) ───────────────────────────────────────

export interface SetLineExpressionArgs {
  dialogId: string;
  line: number;
  spriteId?: string;
  expression: string;
  backToDefault?: boolean;
}

export const setLineExpression: SceneOperation<SetLineExpressionArgs> = {
  name: 'setLineExpression',
  description: 'Set the speaking character\'s expression (one of its states) while a dialog line plays, or "none" to remove it. An expression without a picture still saves: the line plays the default picture until one exists.',
  inputSchema: obj({
    dialogId: str('The dialog id.'),
    line: int('The line\'s 1-based number within that dialog.'),
    spriteId: str('The speaking sprite id (not needed with "none").'),
    expression: str('A state name of that sprite, or "none" to remove the line\'s expression.'),
    backToDefault: bool('Go back to the sprite\'s default state when the line ends; default true.'),
  }, ['dialogId', 'line', 'expression']),
  apply(doc, args) {
    const dialog = doc.dialogs.find((d) => d.id === args.dialogId);
    if (!dialog) return fail([e('not_found', `no dialog ${args.dialogId}`, 'dialogId')]);
    const lines = dialog.lines ?? [];
    if (args.line < 1 || args.line > lines.length) {
      return fail([e('out_of_range', `line must be 1–${lines.length}`, 'line')]);
    }
    const index = args.line - 1;
    const label = `Line ${args.line} of “${dialog.name}”`;
    let line: DialogLine;
    let summary: string;
    if (args.expression === 'none') {
      const { expression: _gone, ...rest } = lines[index];
      line = rest;
      summary = `${label}: no expression`;
    } else {
      if (args.spriteId === undefined) return fail([e('invalid_args', 'spriteId is required with an expression', 'spriteId')]);
      const sprite = spriteById(doc, args.spriteId);
      if (!sprite) return fail([e('not_found', `no sprite ${args.spriteId}`, 'spriteId')]);
      if (!sprite.states.some((s) => s.name === args.expression)) {
        return fail([e('not_found', `no state "${args.expression}" on ${sprite.name}`, 'expression')]);
      }
      const backToDefault = args.backToDefault ?? true;
      line = { ...lines[index], expression: { spriteId: sprite.id, state: args.expression, backToDefault } };
      summary = `${label}: ${sprite.name} ${args.expression}${backToDefault ? `, then back to ${sprite.defaultState}` : ''}`;
    }
    const next: Dialog = { ...dialog, lines: lines.map((l, i) => (i === index ? line : l)) };
    return { ok: true, doc: { ...doc, dialogs: replaceById(doc.dialogs, next) }, summary, createdIds: [] };
  },
};
